import React from 'react';
import {useCallback, useEffect} from 'react';

import scrollIntoView from 'scroll-into-view-if-needed';

const config = {childList: true};

type UseScrollAddedNodesIntoView = {
	enabled?: boolean;
	ref: React.RefObject<HTMLElement>;
};

export default function useScrollAddedNodesIntoView({
	enabled = true,
	ref,
}: UseScrollAddedNodesIntoView) {
	const callback = useCallback<MutationCallback>((mutationsList) => {
		let removed: Node[] = [];
		let added: Node[] = [];
		for (const {addedNodes, removedNodes, type} of mutationsList) {
			if (type !== 'childList') continue;

			removed = removed.concat(Array.from(removedNodes));

			if (!addedNodes.length) continue;

			const el = addedNodes[0] as HTMLElement;
			// If it was removed in this mutation as well, it was just a reorder
			// we only want to scroll into view new items in the container
			if (!removed.includes(el)) {
				added = added.concat(Array.from(addedNodes));
			}
		}

		// If we added multiples lets just ignore
		if (added.length > 1 || added.length === 0) return;

		// TODO: scroll into view if it is not visible somehow?
		// If we want to do that we can use the browser version
		// which will scroll all parent elements
		scrollIntoView(added[0] as HTMLElement, {
			behavior: 'smooth',
			block: 'end',
			scrollMode: 'if-needed',
			skipOverflowHiddenElements: true,
		});
	}, []);

	useEffect(() => {
		if (!ref.current || !enabled) return;

		const observer = new MutationObserver(callback);
		observer.observe(ref.current, config);
		return () => observer.disconnect();
	}, [callback, ref, enabled]);
}
